import { useCallback, useRef } from 'react'
import axios from 'axios'

type EditMode = 'cell' | 'popup'

interface LookupItem {
	value?: any
	text?: any
}

type SetCellValue = (field: string, value: any) => void

export default function usePopupAutoFillCascade(params: {
	folder: string | null
	model: string | null
	editMode: EditMode
	isNewRowRef: { current: boolean }
	onAutoFillLockChange?: (locked: boolean) => void
}) {
	const { folder, model, editMode, isNewRowRef, onAutoFillLockChange } = params

	const autoFillRequestSeqRef = useRef(0)
	const autoFillInProgressRef = useRef(false)

	const isFilledValue = useCallback((value: any) => {
		if (value == null) return false
		if (typeof value === 'string') return value.trim() !== ''
		return true
	}, [])

	const loadDynamicLookupBatch = useCallback(
		async (fieldNames: string[], rowData: any): Promise<Record<string, LookupItem[]>> => {
			if (!folder || !model) return {}
			if (!fieldNames.length) return {}

			const payload = isNewRowRef.current
				? Object.fromEntries(Object.entries(rowData || {}).filter(([, v]) => v != null && v !== ''))
				: rowData || {}

			const fieldsParam = encodeURIComponent(fieldNames.join(','))
			const url = `${API_FIBER_URL}/view/lookup-batch?folder=${folder}&model=${model}&fields=${fieldsParam}&data=${encodeURIComponent(
				JSON.stringify(payload)
			)}`

			const response = await axios.get(url)
			return response?.data?.lookups ?? {}
		},
		[folder, model, isNewRowRef]
	)

	const runAutoFillCascade = useCallback(
		async (rowData: any, cascadeFields: string[], setCellValue?: SetCellValue) => {
			if (!isNewRowRef.current || editMode !== 'popup' || cascadeFields.length === 0) {
				return rowData
			}

			const reqId = ++autoFillRequestSeqRef.current
			const nextRowData = { ...(rowData || {}) }

			autoFillInProgressRef.current = true
			onAutoFillLockChange?.(true)

			let changed = true
			let passes = 0
			try {
				while (changed && passes < 4) {
					changed = false
					passes++

					const missingFields = cascadeFields.filter(depField => !isFilledValue(nextRowData[depField]))
					if (!missingFields.length) {
						continue
					}

					const lookupsByField = await loadDynamicLookupBatch(missingFields, nextRowData)

					// Ignore results from stale requests.
					if (reqId !== autoFillRequestSeqRef.current) {
						return nextRowData
					}

					for (const depField of missingFields) {
						const lookup = lookupsByField?.[depField] ?? []

						if (lookup.length !== 1) {
							continue
						}

						const single = lookup[0]
						const singleValue = single?.value ?? single?.text
						if (!isFilledValue(singleValue)) {
							continue
						}

						nextRowData[depField] = singleValue
						changed = true

						setCellValue?.(depField, singleValue)
					}
				}
			} finally {
				if (reqId === autoFillRequestSeqRef.current) {
					autoFillInProgressRef.current = false
					onAutoFillLockChange?.(false)
				}
			}

			return nextRowData
		},
		[editMode, isFilledValue, loadDynamicLookupBatch, isNewRowRef]
	)

	return {
		autoFillInProgressRef,
		runAutoFillCascade,
		loadDynamicLookupBatch,
	}
}
